import type {
	PropertyFilters,
	ShortsFilters
} from '~/core/entities/property/filters';
import type { CompleteFiltersAppliedClick } from '~/types/mixpanel';

import { until } from '@vueuse/core';
import { useEventDataFilters } from '~/composables/events/useEventDataFilters/useEventDataFilters';
import { useTrackSearchPropertyEvents } from '~/composables/events/useTrackSearchPropertyEvents';
import { waitForUrlSync } from '~/composables/useRouteQueryHelpers';
import { MixpanelProps } from '~/core/service/mixpanel';
import { getChangedKeysFilters } from '~~/utils';
import { reactive } from 'vue';

export type ShortsOpenFilterSnapshotId = 'pricing' | 'area' | 'propertyTypes';

export type ShortsPricingFilterSnapshot = Pick<
	ShortsFilters,
	'minAskingPrice' | 'maxAskingPrice'
>;

export type ShortsAreaFilterSnapshot = Pick<
	ShortsFilters,
	'minArea' | 'maxArea'
>;

export type ShortsPropertyTypesSnapshot = NonNullable<
	ShortsFilters['propertyTypes']
>;

export interface ShortsOpenFilterSnapshots {
	pricing: ShortsPricingFilterSnapshot;
	area: ShortsAreaFilterSnapshot;
	propertyTypes: ShortsPropertyTypesSnapshot;
}

function emptyPricingSnapshot(): ShortsPricingFilterSnapshot {
	return { minAskingPrice: undefined, maxAskingPrice: undefined };
}

function emptyAreaSnapshot(): ShortsAreaFilterSnapshot {
	return { minArea: undefined, maxArea: undefined };
}

function shortsFiltersAsPropertyFiltersForDiff(
	f: ShortsFilters
): PropertyFilters {
	return f as PropertyFilters;
}

export function useShortsFiltersTracking(
	toShortsFilters: () => ShortsFilters,
	toPreviewFilters: () => ShortsFilters,
	countProperties: () => number,
	isFetchingProperties: () => boolean
) {
	const { trackFilterApplied, trackFiltersCleared } =
		useTrackSearchPropertyEvents();
	const { getFilterAppliedValues, getFilterClearedEventStatus } =
		useEventDataFilters();

	const snapshots = reactive<ShortsOpenFilterSnapshots>({
		pricing: emptyPricingSnapshot(),
		area: emptyAreaSnapshot(),
		propertyTypes: []
	});

	const route = useRoute();

	async function dispatchFilterAppliedEvent(
		pf: PropertyFilters,
		source: 'openFilters' | 'moreFilters',
		changedKeys: string[]
	) {
		await waitForFetchToComplete();
		const filterAppliedValues = getFilterAppliedValues(pf, source, changedKeys);

		trackFilterApplied({
			...filterAppliedValues,
			[MixpanelProps.CURRENT_PAGE]: 'Shorts Home',
			[MixpanelProps.TOTAL_RESULTS]: countProperties()
		} as CompleteFiltersAppliedClick);
	}

	function takeSnapshot(filterId: ShortsOpenFilterSnapshotId) {
		const f = toShortsFilters();
		switch (filterId) {
			case 'pricing':
				Object.assign(snapshots.pricing, {
					minAskingPrice: f.minAskingPrice,
					maxAskingPrice: f.maxAskingPrice
				});
				break;
			case 'area':
				Object.assign(snapshots.area, {
					minArea: f.minArea,
					maxArea: f.maxArea
				});
				break;
			case 'propertyTypes':
				snapshots.propertyTypes = [...(f.propertyTypes ?? [])];
				break;
			default: {
				const _exhaustive: never = filterId;
				return _exhaustive;
			}
		}
	}

	async function waitForFetchToComplete() {
		await until(isFetchingProperties).toBeTruthy();
		await until(() => !isFetchingProperties()).toBeTruthy();
	}

	async function handleTrackOpenFilterApplied(
		filterId: ShortsOpenFilterSnapshotId
	) {
		/**
		 * `pricing` e `area` já são rastreados por `useShortsFilterChangeTracking`
		 * após o término do fetch (com total_results final). Evita evento duplicado.
		 */
		if (filterId === 'pricing' || filterId === 'area') {
			return;
		}

		const oldFilters: ShortsFilters = { ...toShortsFilters() };

		switch (filterId) {
			case 'propertyTypes':
				oldFilters.propertyTypes = snapshots.propertyTypes.length
					? [...snapshots.propertyTypes]
					: undefined;
				break;
			default: {
				const _exhaustive: never = filterId;
				return _exhaustive;
			}
		}

		let currentFilters = toShortsFilters();
		let currentPf = shortsFiltersAsPropertyFiltersForDiff(currentFilters);
		const oldPf = shortsFiltersAsPropertyFiltersForDiff(oldFilters);
		let changedKeys = getChangedKeysFilters(currentPf, oldPf);

		// Awaiting route.query resolution actively or fallback after timeout
		if (changedKeys.length === 0) {
			await waitForUrlSync(route);

			currentFilters = toShortsFilters();
			currentPf = shortsFiltersAsPropertyFiltersForDiff(currentFilters);
			changedKeys = getChangedKeysFilters(currentPf, oldPf);
		}

		if (changedKeys.length > 0) {
			dispatchFilterAppliedEvent(
				currentPf,
				'openFilters',
				changedKeys as string[]
			);
		}
	}

	async function trackApplySidebarFilters() {
		const oldFilters = toShortsFilters();
		const newFilters = toPreviewFilters();
		const newPf = shortsFiltersAsPropertyFiltersForDiff(newFilters);
		const oldPf = shortsFiltersAsPropertyFiltersForDiff(oldFilters);
		const changedKeys = getChangedKeysFilters(newPf, oldPf);

		if (changedKeys.length > 0) {
			await dispatchFilterAppliedEvent(
				newPf,
				'moreFilters',
				changedKeys as string[]
			);
		}
	}

	async function trackClearSidebarFilters() {
		const filtersCopy = toShortsFilters();
		const previousResults = countProperties();

		await until(isFetchingProperties).toBeTruthy();
		await until(() => !isFetchingProperties()).toBeTruthy();

		const fullPayload = getFilterClearedEventStatus(
			shortsFiltersAsPropertyFiltersForDiff(filtersCopy),
			previousResults
		);

		trackFiltersCleared({
			[MixpanelProps.NEIGHBORHOODS_CLEARED]:
				fullPayload[MixpanelProps.NEIGHBORHOODS_CLEARED],
			[MixpanelProps.PRICE_CLEARED]: fullPayload[MixpanelProps.PRICE_CLEARED],
			[MixpanelProps.AREA_CLEARED]: fullPayload[MixpanelProps.AREA_CLEARED],
			[MixpanelProps.PROPERTY_TYPE_CLEARED]:
				fullPayload[MixpanelProps.PROPERTY_TYPE_CLEARED],
			[MixpanelProps.PREVIOUS_RESULTS]: previousResults,
			[MixpanelProps.TOTAL_RESULTS]: countProperties()
		});
	}

	return {
		takeSnapshot,
		handleTrackOpenFilterApplied,
		trackApplySidebarFilters,
		trackClearSidebarFilters
	};
}
